<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { onMount } from 'svelte';
    import { SVG } from '@svgdotjs/svg.js';
    import setupDroparea from './util/actions/setupDroparea.js';
    import CircleHotspotChoice from '../CircleHotspotChoice.svelte';
    import EllipseHotspotChoice from '../EllipseHotspotChoice.svelte';
    import RectHotspotChoice from '../RectHotspotChoice.svelte';
    import PolyHotspotChoice from '../PolyHotspotChoice.svelte';
    import { getActualKey, remToPx, generateElementId } from '@oat-sa-private/ui-core';

    /**
     * Component intended to render gap in answer area
     *
     * @property {string} identifier - gap identifier
     * @property {string} shape - graphical shape of gap
     * @property {number[]} coords - array of gap shape coordinates
     * @property {string} hotspotLabel - the alternative text for this (hot) area of the image
     * @property {object} dropareaRegistry - 'dropAreaRegistryFactory' object shared for this drag group (for interaction)
     * @property {string} ariaLabel - aria-label
     * @property {string} tabindex
     * @property {boolean} targetable - style when element that can be dropped here is being dragged (for click/tap, element is selected)
     * @property {boolean} targeted - style when element that can be dropped here is being dragged over this item (for click/tap, element is selected and item is hovered)
     * @property {boolean} disabled
     * @property {boolean} invisible - make it transparent unless targeted or keyboard-focused
     * @property {string} instructions - instructions for assistive technology devices
     * @property {string} instructionsLang - the lang of the test taker
     */

    let {
        identifier,
        shape,
        coords,
        hotspotLabel,
        dropareaRegistry,
        ariaLabel,
        tabindex,
        targetable = false,
        targeted = false,
        disabled = false,
        invisible = false,
        instructions,
        instructionsLang,

        onclick,
        onkeySelect,
        onhoverOver,
        onhoverOut,
        onmount,
        oncenter,
        ondragOver,
        ondragOut,
        ondrop
    } = $props();

    const shapeToComponent = Object.freeze({
        circle: CircleHotspotChoice,
        ellipse: EllipseHotspotChoice,
        rect: RectHotspotChoice,
        poly: PolyHotspotChoice
    });
    const borderDashWidth = remToPx(0.75);
    const borderDotWidth = remToPx(0.25);

    let ShapeComponent = $derived(shapeToComponent[shape]);

    let instructionsEltId = $derived(instructions && generateElementId('hotspot-choice-label'));

    let groupElement = $state();

    onMount(() => {
        SVG(groupElement);
    });

    /**
     * Handle choice mouse click
     */
    function handleClick() {
        onclick?.({ key: identifier });
    }

    /**
     * Handle choice keySelect
     * @param {KeyboardEvent} e
     */
    function handleKeyUp(e) {
        const actualKey = getActualKey(e);
        if (actualKey === 'space' || actualKey === 'enter') {
            onkeySelect?.({ key: identifier });
            e.preventDefault();
        }
    }

    /**
     * Handle choice mouseenter (hover)
     */
    function handleMouseenter() {
        onhoverOver?.({ key: identifier });
    }

    /**
     * Handle choice mouseleave
     */
    function handleMouseleave() {
        onhoverOut?.({ key: identifier });
    }

    /**
     * Handle choice mount
     * @param {Object} e
     */
    function handleMount(e) {
        onmount?.({ key: identifier, svgGroup: e.svgGroup });
    }

    /**
     * Handle choice center (fired by polygon shape)
     * @param {Object} e
     */
    function handleCenter(e) {
        oncenter?.({ key: identifier, cx: e.cx, cy: e.cy });
    }
</script>

<style>
    .associable-hotspot {
        --outer-frame-border-width: 0.5rem;
        --frame-border-width: 0.25rem;

        /**** default state styles *****/
        outline: none;

        &:disabled {
            cursor: not-allowed;
        }

        & :global(.shape) {
            outline: none;
        }

        & :global(.shape-outer-border) {
            fill: transparent;
            stroke: var(--color-bg-default);
            stroke-width: var(--outer-frame-border-width);
            filter: var(--shadow-filter-url);
        }

        & :global(.shape-inner-border) {
            fill: var(--color-brand);
            fill-opacity: 0.15;
            stroke: var(--color-gs-dark);
            stroke-width: var(--frame-border-width);
        }

        & :global(.shape-dashed-outline) {
            fill: transparent;
            stroke: transparent;
            stroke-width: var(--frame-border-width);
            stroke-dasharray: var(--dash-width) var(--draw-focus-dasharray);
            stroke-linejoin: round;
        }

        /**** polygon hotspot styles *****/

        & :global(g.shape.poly:not(.thin-poly):hover),
        &:focus-visible :global(g.shape.poly:not(.thin-poly)) {
            & :global(.shape-outer-border.shape-outer-border) {
                stroke-width: 1rem;
            }
            & :global(.shape-inner-border.shape-inner-border) {
                stroke-width: 1.5rem;
            }
        }
        & :global(g.shape.thin-poly:hover),
        &:focus-visible :global(g.shape.thin-poly) {
            & :global(.shape-outer-border.shape-outer-border) {
                stroke-width: 1.25rem;
            }
            & :global(.shape-inner-border.shape-inner-border) {
                stroke-width: 0.25rem;
            }
        }

        /****** active state styles *******/
        &.targetable {
            cursor: pointer;
        }
        &.targetable :global(.shape-inner-border) {
            stroke: var(--color-brand);
        }

        /****** targeted state styles *******/
        &.targeted :global(.shape-dashed-outline) {
            stroke: var(--color-brand-hover);
        }
    }

    /****** focus styles *******/
    :global(.associable-hotspot:focus-visible) {
        & :global(.shape-dashed-outline) {
            stroke: var(--color-brand-hover);
            stroke-dasharray: var(--draw-focus-dasharray);
        }
    }

    /****** invisible styles *******/
    :global(.associable-hotspot.invisible:not(.targeted):not(:focus-visible)) {
        & :global(.shape-outer-border),
        & :global(.shape-inner-border),
        & :global(.shape-outline-cover),
        & :global(.shape-shadow) {
            stroke: transparent;
            fill: transparent;
        }
    }
</style>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<g
    key={identifier}
    class="associable-hotspot"
    style={`--draw-focus-dasharray:${borderDotWidth}px;--dash-width:${borderDashWidth}px;`}
    class:targetable
    class:targeted
    class:invisible
    tabindex={!disabled ? tabindex : void 0}
    bind:this={groupElement}
    use:setupDroparea={{ dropareaRegistry, key: identifier }}
    onmouseenter={!disabled ? handleMouseenter : void 0}
    onmouseleave={!disabled ? handleMouseleave : void 0}
    onkeyup={!disabled ? handleKeyUp : void 0}
    {ondragOver}
    {ondragOut}
    {ondrop}>
    <ShapeComponent
        {coords}
        {disabled}
        label={hotspotLabel}
        {invisible}
        {targeted}
        {ariaLabel}
        ariaLabelledBy={instructionsEltId}
        selected={false}
        checkmark={false}
        onmount={handleMount}
        oncenter={handleCenter}
        onclick={!disabled ? handleClick : void 0} />
    {#if instructions}
        <text id={instructionsEltId} class="hidden" lang={instructionsLang}>{instructions}</text>
    {/if}
</g>
